import { useEffect, useState, type ReactNode } from "react"
import { Box, Button, Fade, Stack, Typography } from "@mui/material"
import { keyframes } from "@mui/material/styles"
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import StatusRing, { type FeedbackStatus } from "./status-ring"

export type { FeedbackStatus }

const DELAY_TEXTO_S = 0.55

const surgir = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

export interface StatusFeedbackProps {
    status: FeedbackStatus | null
    titulo: string
    detalhe?: string
    imagem?: string | ReactNode
    textoBotao?: string
    onDismiss?: () => void
    autoRetornoMs?: number | null
    iconeBotao?: ReactNode
    acaoSecundaria?: { rotulo: string; aoClicar: () => void; icone?: ReactNode }
    animarEntrada?: boolean
    /** Dentro de um passo da tela (pagamento do checkout), em vez de cobrir a tela inteira. */
    embutido?: boolean
}

export default function StatusFeedback({ status, titulo, detalhe, imagem, textoBotao, onDismiss, autoRetornoMs = 8000, iconeBotao, acaoSecundaria, animarEntrada = true, embutido = false, }: StatusFeedbackProps): JSX.Element {
    const [exibido, setExibido] = useState<{
        status: FeedbackStatus
        titulo: string
        detalhe?: string
    }>({ status: 'loading', titulo, detalhe })

    const ultimoStatus = exibido.status
    const autoRetornoAtivo = !!status && status !== 'loading' && autoRetornoMs != null && !!onDismiss
    const [segundosRestantes, setSegundosRestantes] = useState<number | null>(null)
    const carregando = ultimoStatus === 'loading'
    const mostrarBotao = !carregando && !!textoBotao && !!onDismiss

    useEffect(() => {
        if (status) setExibido({ status, titulo, detalhe })
    }, [status, titulo, detalhe])

    useEffect(() => {
        if (!autoRetornoAtivo || autoRetornoMs == null) {
            setSegundosRestantes(null)
            return
        }

        setSegundosRestantes(Math.ceil(autoRetornoMs / 1000))

        const intervalo = setInterval(() => {
            setSegundosRestantes((prev) => (prev == null ? prev : Math.max(prev - 1, 0)))
        }, 1000)

        const timer = setTimeout(() => onDismiss?.(), autoRetornoMs)

        return () => {
            clearInterval(intervalo)
            clearTimeout(timer)
        }
    }, [autoRetornoAtivo, status, autoRetornoMs, onDismiss])

    return (
        <Fade in={!!status} appear={animarEntrada} timeout={400} unmountOnExit>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Embutido, o fundo é o da área do passo: pintar o próprio fazia a cor mudar de uma etapa para outra.
                    ...(embutido
                        ? { position: 'relative', width: '100%', flex: '1 0 auto' }
                        : { inset: 0, position: 'fixed', zIndex: (theme) => theme.zIndex.modal, backgroundColor: 'background.default' }),
                }}
            >
                <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', px: 4, maxWidth: 760 }}>
                    {imagem == null ? (
                        <StatusRing status={ultimoStatus} />
                    ) : typeof imagem === 'string' ? (
                        <Box
                            component="img"
                            src={imagem}
                            alt=""
                            sx={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        imagem
                    )}
                    <Stack
                        key={ultimoStatus}
                        spacing={1.5}
                        sx={{
                            alignItems: 'center',
                            animation: `${surgir} 0.35s ease-out ${carregando ? 0 : DELAY_TEXTO_S}s both`,
                        }}
                    >
                        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                            {exibido.titulo}
                        </Typography>

                        {exibido.detalhe && (
                            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 400 }}>
                                {exibido.detalhe}
                            </Typography>
                        )}
                    </Stack>

                    <Box sx={{ minHeight: 72, mt: 4, display: 'flex', alignItems: 'center' }}>
                        {!!textoBotao && !!onDismiss && (
                            <Fade in={mostrarBotao} timeout={400} style={{ transitionDelay: `${DELAY_TEXTO_S * 1000}ms` }}>
                                <Stack spacing={1.5} sx={{ alignItems: acaoSecundaria ? 'stretch' : 'center' }}>
                                    <Button
                                        size="large"
                                        variant="contained"
                                        onClick={onDismiss}
                                        startIcon={iconeBotao ?? <ReplayRoundedIcon />}
                                        sx={{ py: 2, px: 6, borderRadius: 3, textTransform: 'none', fontSize: '1.5rem' }}
                                    >
                                        {textoBotao}
                                    </Button>

                                    {acaoSecundaria && (
                                        <Button
                                            size="large"
                                            variant="outlined"
                                            color="inherit"
                                            onClick={acaoSecundaria.aoClicar}
                                            startIcon={acaoSecundaria.icone}
                                            sx={{ py: 2, px: 6, borderRadius: 3, textTransform: 'none', fontSize: '1.5rem' }}
                                        >
                                            {acaoSecundaria.rotulo}
                                        </Button>
                                    )}

                                    {segundosRestantes != null && (
                                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            {segundosRestantes > 0
                                                ? `Retornando em ${segundosRestantes}s`
                                                : 'Retornando...'}
                                        </Typography>
                                    )}
                                </Stack>
                            </Fade>
                        )}
                    </Box>
                </Stack>
            </Box>
        </Fade>
    )
}